import { useWebsiteSettings } from 'client/store'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { PageTypeEnum, WebsitePageType } from 'common/enums/PageTypeEnum'
import React from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { BaseError } from 'src/editor/api/errors/base-error'
import { createWebsitePage } from 'src/editor/api/website-pages-api'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { ModalWindow } from 'src/editor/shared/ui-lib/modal-window'
import { PageElement } from 'src/editor/shared/ui-lib/page-element'
import { getWebsiteDomain } from 'src/editor/store/website-settings/website-settings-selector'

export interface CreatePageModalProps {
  isVisible: boolean
  websiteId?: number
  websiteLocale: PageLocaleEnum
  onClose?: () => void
}

export const CreatePageModal = ({
  isVisible,
  websiteId,
  websiteLocale,
  onClose,
}: CreatePageModalProps) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = React.useState(false)
  const websiteDomain = useWebsiteSettings(getWebsiteDomain)

  const [selectedPageType, setSelectedPageType] =
    React.useState<WebsitePageType>()
  const [name, setName] = React.useState('')
  const [url, setUrl] = React.useState('')

  const handleCreatePage = async () => {
    setIsLoading(true)

    if (!selectedPageType || !name || !url || !websiteId) {
      setIsLoading(false)
      return
    }

    try {
      await createWebsitePage({
        websiteId,
        pageType: selectedPageType,
        name,
        url,
        locale: websiteLocale,
      })
      toast.success(
        t('website_settings.sidebar.page_settings.created_successfully'),
      )
    } catch (error) {
      if (error instanceof BaseError) {
        toast.error(error?.response?.detail)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(`https://${websiteDomain}/${url}`)
  }

  return (
    <ModalWindow
      isOpened={isVisible}
      onClose={onClose}
      title={t('pages_management.create_page_title')}
      cancelButtonProps={{
        onClick: onClose,
        children: t('global.cancel'),
      }}
      confirmButtonProps={{
        onClick: () => {
          handleCreatePage()
        },
        isLoading,
        children: t('core.header.save'),
      }}
    >
      <TextInputControl
        wrapperProps={{
          className: 'mt-1 mb-3',
          labelText: t('pages_management.create_page_name_label'),
          required: true,
        }}
        id="name"
        value={name}
        update={value => setName(value)}
      />
      <TextInputControl
        wrapperProps={{
          className: 'w-full mt-1 mb-3',
          labelText: t('pages_management.create_page_url_label'),
          required: true,
        }}
        appendix={`https://${websiteDomain}/`}
        id="url"
        value={url}
        update={value => setUrl(value)}
        onCopy={handleCopyUrl}
      />
      <h4 className="text-heading-4 text-textPrimary">
        <span>{t('pages_management.page_type_label')}</span>
        <span className="text-error-500"> *</span>
      </h4>
      <div>
        <PageElement
          isSelected={selectedPageType === PageTypeEnum.WebsiteHome}
          onSelect={() => setSelectedPageType(PageTypeEnum.WebsiteHome)}
          name={t('website.page_types.website_home')}
          type={PageTypeEnum.WebsiteHome}
        />
        <PageElement
          isSelected={selectedPageType === PageTypeEnum.WebsiteDefault}
          onSelect={() => setSelectedPageType(PageTypeEnum.WebsiteDefault)}
          name={t('website.page_types.website_static')}
          type={PageTypeEnum.WebsiteDefault}
        />
      </div>
    </ModalWindow>
  )
}
